/* eslint-disable react-hooks/exhaustive-deps */
import { Accordion, Button, Tooltip } from '@cambridgeassessment/cambridge-ui';
import { BUSINESS_UNITS, ComponentType, Roles } from '@cambridgeassessment/lib-ee-commons';
import { noop } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AddIcon from '@material-ui/icons/Add';
import { LocalSection, Component, Section } from '../../../../../service/components/types';
import thunks from '../../../../../store/components/thunks';
import SectionFC from '../SectionFC';
import {
  StyledAccordion,
  StyledAddButtonContainer,
  StyledComponentHeaderContainer,
  StyledComponentHeaderText,
  StyledExpansionPanelDetails,
  StyledReadOnlyComponent,
} from '../styles';
import { StyledLocalSeriesContainer } from './styles';
import { RootState } from '../../../../../store';
import { Authorized } from 'lib-browser';

interface Props {
  readOnly?: boolean;
  component: Component;
  componentId: string;
  removeEnabled: boolean;
  disabled?: boolean;
  withSections?: boolean;
  localSections: LocalSection[];
  setLocalSections: (sections: LocalSection[]) => void;
}

const Sections = (props: Props): JSX.Element => {
  const { component, componentId, removeEnabled, disabled, withSections, localSections, setLocalSections } = props;

  const { businessUnit, selectedRole } = useSelector((state: RootState) => state.userData);

  const dispatch = useDispatch();
  const [initialSectionsLength, setInitialSectionsLength] = useState(component.sections.length);
  const [openedSections, setOpenedSections] = useState<number[]>([]);

  const checkIfNameAlreadyExists = (name: string, sectionId: number): boolean => {
    const { sections } = component;

    if (sectionId === 0 && localSections.length === 1) {
      return !!(sections && sections.find((section: Section) => section.name === name));
    }

    return !!(
      sections &&
      sections.find((section: Section, currentIndex: number) => currentIndex !== sectionId && section.name === name)
    );
  };

  const createNewSection = (componentId: string, section: Section | LocalSection): void => {
    dispatch(thunks.createSection(componentId, section));
    setLocalSections([]);
  };

  const renameSection = (componentId: string, section: LocalSection | Section): void => {
    const updatedSection = section as Section;
    dispatch(thunks.updateSection(componentId, section as Section, 'name', updatedSection.id));
  };

  const deleteSection = (componentId: string, id: number): void => {
    dispatch(thunks.deleteSection(componentId, id));
    setInitialSectionsLength(initialSectionsLength - 1);
  };

  const allowMultipleFiles = component.type === ComponentType.MODERATED && businessUnit?.id === BUSINESS_UNITS.OCR.id;

  const createLocalSection = (): void => {
    const arr = [{ name: '', fileTypes: [], allowMultipleFiles }, ...localSections];
    setLocalSections(arr);
  };

  const deleteLocalSection = (): void => setLocalSections([]);

  useEffect(() => {
    setOpenedSections(component.sections.length ? [component.sections[0].id] : []);
  }, []);

  useEffect(() => {
    if (initialSectionsLength !== component.sections.length && component.sections.length > 0) {
      setOpenedSections([...openedSections, component.sections[component.sections.length - 1].id]);
      setInitialSectionsLength(component.sections.length);
    }
  }, [component.sections.length]);

  const doesSectionsExist = component.sections.length > 0;
  const sortedSections = useMemo(() => component.sections.sort((a, b) => a.id - b.id), [component.sections]);

  return (
    <StyledExpansionPanelDetails>
      <StyledComponentHeaderContainer>
        <StyledComponentHeaderText>Learner Sections</StyledComponentHeaderText>
      </StyledComponentHeaderContainer>
      The pieces of work that each candidate is expected to complete for the unit/component.
      {doesSectionsExist &&
        sortedSections.map((section: Section, i: number) => {
          const sectionId = `${section.name}-${i}`;

          return (
            <StyledAccordion key={sectionId} data-qa-id={`section-${section.id}`}>
              <Accordion
                key={sectionId}
                headerContent={section.name}
                dataQaId='section'
                expanded={openedSections.includes(section.id)}
                onClick={() => {
                  if (openedSections.includes(section.id)) {
                    setOpenedSections(openedSections.filter((x) => x !== section.id));
                  } else {
                    setOpenedSections([...openedSections, section.id]);
                  }
                }}
              >
                <SectionFC
                  key={sectionId}
                  section={section}
                  submit={renameSection}
                  remove={deleteSection}
                  componentId={componentId}
                  componentName={component.name}
                  index={i}
                  removeEnabled={removeEnabled}
                  deleteLocalSection={noop}
                  checkName={checkIfNameAlreadyExists}
                  disabled={disabled}
                />
              </Accordion>
            </StyledAccordion>
          );
        })}
      <StyledLocalSeriesContainer>
        {localSections.map((section: LocalSection, i: number) => (
          <SectionFC
            key={`${section.name}-${i}`}
            section={section}
            submit={(componentId, section) => {
              createNewSection(componentId, section);
            }}
            remove={deleteLocalSection}
            componentId={componentId}
            componentName={component.name}
            index={i}
            removeEnabled={removeEnabled}
            checkName={checkIfNameAlreadyExists}
            deleteLocalSection={deleteLocalSection}
            disabledMultipleCandidatesSwitch={true}
            disableMultipleFilesSwitch={true}
            disabledFileTypes={true}
            isLocalSection
          />
        ))}
      </StyledLocalSeriesContainer>
      {withSections && (
        <Authorized
          selectedRole={selectedRole}
          roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR]}
          readOnlyRoles={[Roles.SUBMIT_COMPLIANCE, Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER]}
        >
          <StyledReadOnlyComponent
            data-qa-id={`read-only-button-wrapper-${props?.readOnly}`}
            readOnly={props?.readOnly}
          >
            <Tooltip
              arrow={true}
              placement='bottom-start'
              dataQaId='group-upload'
              title='Configure for each piece of work, and whether they are checked for plagiarism.'
              enterDelay={500}
            >
              <StyledAddButtonContainer>
                <Button
                  onClick={createLocalSection}
                  color='primary'
                  qaId={`expansion-details-add-button-${component.name}`}
                  variant='outlined'
                  type='submit'
                  size='medium'
                  disabled={localSections.length > 0 || disabled}
                  startIcon={<AddIcon />}
                >
                  Add learner section
                </Button>
              </StyledAddButtonContainer>
            </Tooltip>
          </StyledReadOnlyComponent>
        </Authorized>
      )}
    </StyledExpansionPanelDetails>
  );
};

export default Sections;
